import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import {
  type DocumentNode,
  type OperationDefinitionNode,
  parse,
} from 'graphql';
import { GraphQLClient } from 'graphql-request';
import { t } from 'i18next';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { clearAuthTokens, getAccessToken } from '@/lib/tokenStorage';

type GraphQLErrorLike = {
  message?: string;
  extensions?: { code?: string; statusCode?: number };
};

type GraphQLResponseMeta = {
  response?: {
    status?: number;
    errors?: GraphQLErrorLike[];
  };
};

type NormalizedQueryError = {
  message?: string;
  status?: number | string;
  statusCode?: number;
  response?: { status?: number };
  data?: { message?: string };
};

type GraphQLRequestArgs = {
  document?: string | DocumentNode;
  variables?: Record<string, unknown>;
};

function getQueryName(document: GraphQLRequestArgs['document']): string {
  if (!document) return 'Unknown Query';
  try {
    const ast = typeof document === 'string' ? parse(document) : document;
    const operation = ast.definitions.find(
      (def): def is OperationDefinitionNode =>
        def.kind === 'OperationDefinition' && Boolean(def.name),
    );
    return operation?.name?.value ?? 'Unknown Query';
  } catch {
    return 'Unknown Query';
  }
}

const getGraphQLErrors = (meta: unknown): GraphQLErrorLike[] =>
  (meta as GraphQLResponseMeta | undefined)?.response?.errors ?? [];

const getResponseStatus = (meta: unknown): number | undefined =>
  (meta as GraphQLResponseMeta | undefined)?.response?.status;

function stripClientErrorBlob(message: string): string {
  const idx = message.indexOf(': {"response"');
  return (idx >= 0 ? message.slice(0, idx) : message).trim();
}

const hasGraphQLAuthError = (errors: GraphQLErrorLike[]): boolean =>
  errors.some(
    (e) =>
      e.extensions?.code === 'UNAUTHENTICATED' ||
      e.extensions?.statusCode === 401,
  );

const messageIndicatesAuthError = (message?: string): boolean => {
  if (typeof message !== 'string') return false;
  const lower = message.toLowerCase();
  return (
    lower.includes('401 unauthorized') ||
    lower.includes('status: 401') ||
    lower.includes('status code: 401') ||
    lower.includes('unauthenticated')
  );
};

function resolveErrorDetail(
  errors: GraphQLErrorLike[],
  error: NormalizedQueryError,
): string {
  const first = errors[0];
  const code = first?.extensions?.code;
  const message =
    first?.message ||
    (typeof error.message === 'string'
      ? stripClientErrorBlob(error.message)
      : undefined) ||
    error.data?.message ||
    t('common.unknownError');
  return code ? `${code}: ${message}` : message;
}

const customGraphqlRequestBaseQuery = () => {
  const client = new GraphQLClient(import.meta.env.VITE_GRAPHQL_API, {
    credentials: 'include',
    headers: {
      'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
    },
    fetch: async (url, options) => {
      const response = await fetch(url, options);

      if (response.status === 401) {
        console.error('[GraphQL Fetch]: 401 Unauthorized - Token expired');
        clearAuthTokens();
        window.location.href = '/login';
      }

      return response;
    },
  });

  const baseQuery = graphqlRequestBaseQuery({
    client,
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  const baseQueryWithAuthHandling: BaseQueryFn = async (
    args,
    api,
    extraOptions,
  ) => {
    let result;

    const requestArgs = args as GraphQLRequestArgs;
    const queryName = getQueryName(requestArgs?.document);
    const variables = requestArgs?.variables || {};

    try {
      result = await baseQuery(args, api, extraOptions);
    } catch (caught: unknown) {
      const error = caught as NormalizedQueryError;
      console.error(`[RTK Query Caught Error] Query: ${queryName}`, {
        error,
        variables,
        status: error?.response?.status || error?.status || error?.statusCode,
      });
      const status =
        error?.response?.status || error?.status || error?.statusCode;

      if (status === 401 || status === '401') {
        console.error(
          `[RTK Query Caught Auth Error] 401 Unauthorized on query: ${queryName}`,
          { variables },
        );
        clearAuthTokens();
        window.location.href = '/login';
      }

      throw error;
    }

    if (result.error) {
      const error = result.error as NormalizedQueryError;
      const graphQLErrors = getGraphQLErrors(result.meta);

      console.error(`[RTK Query Error] Query: ${queryName}`, {
        errorObject: result.error,
        graphQLErrors,
        meta: result.meta,
      });

      const status =
        error?.status ||
        error?.statusCode ||
        error?.response?.status ||
        getResponseStatus(result.meta);

      const isAuthError =
        status === 401 ||
        status === '401' ||
        messageIndicatesAuthError(error?.message) ||
        hasGraphQLAuthError(graphQLErrors);

      if (isAuthError) {
        console.error(
          `[RTK Query Auth Error] LOGOUT TRIGGERED - Query: ${queryName}`,
        );
        clearAuthTokens();
        window.location.href = '/login';
        return result;
      }

      const summary = `${t('common.error')}: ${queryName}`;
      const detail = resolveErrorDetail(graphQLErrors, error);

      api.dispatch(
        addToast({
          severity: 'error',
          summary,
          detail,
          life: ToastLife.MEDIUM,
        }),
      );
    }

    return result;
  };

  return baseQueryWithAuthHandling;
};

export const api = createApi({
  baseQuery: customGraphqlRequestBaseQuery(),
  endpoints: () => ({}),
});

export const enhancedApi = api.enhanceEndpoints({
  addTagTypes: [
    'AspirationSites',
    'Auth',
    'AvgData',
    'Calves',
    'ChuteSchedules',
    'Connections',
    'DonorSchedules',
    'AllTopRevisions',
    'LegacyApps',
    'LegacyAppsFavorites',
    'Lookups',
    'Me',
    'OpuEntries',
    'Partnerships',
    'PredictionTechs',
    'CurrentFertPredictionTech',
    'ShipmentGroupMasters',
    'SplitFertilizationList',
    'CommDairyFertilizationList',
    'FertilizationListFreshShipment',
    'FreshStrawFrozenEmbryos',
    'FreshStrawFlushes',
    'FreshStrawTripMeta',
    'FreshStrawEmbryoPartnerships',
    'TechRelationships',
    'FertilizationQcData',
    'ShipGroupList',
    'StatsData',
    'FreshShipmentList',
    'EstimationHeaderVal',
    'QcFreshShipment',
    'Sites',
    'TravelingTechs',
    'FertTechs',
    'FertilizationListFreeze',
    'EmbryosFreeze',
    'EmbryoListFreezeTemp',
    'IT_REQUEST',
    'IT_REQUEST_ISSUE_MANAGERS',
    'View',
    'CsrDay7',
    'PseDay7',
    'PseDay7Matings',
    'Day7FrozenEmbryos',
    'Sires',
    'FertWorkSessions',
    'FertWorkSessionDetail',
    'FertWorkRuns',
    'FertWorkUnassignedDonors',
    'FertWorkLabels',
    'FeatureFlags',
    'InClinicEmbryos',
    'InClinicFrozenEmbryos',
    'InClinicRecipQueue',
    'InClinicRecips',
    'InClinicRecipProcs',
    'InClinicLookups',
    'WorkflowHistory',
  ],
  endpoints: {},
});
